/***
 *
 * This constants file is used to add further
 * enrichment to the intergation item.
 *
 * The individual items in this list are merged into
 * the data_source item that is retreived from redux.
 * This merge happens inside of IntegrationsListItem.tsx
 *
 * You can use this to add additional functionality to the Integration Card
 * or override exisiting attributes (aka - If i want to use a frontend image
 * as the logo, We can add a 'logo' property)
 *
 * Currently Extended features:
 *  flag: <string> - the feature flag from LaunchDarkly, if present, will render only if true
 *  badge: <string> - adds a badge next to the name ('New' | 'Beta')
 *  premium: boolean - default false - adds a premium badge next to name
 *  surfaceAI: boolean - default false - adds a surfaceAI badge next to name
 *  aiToolkit: boolean - default false - adds a aiToolkit badge next to name
 *  categories: [<string>] - list of categories for the integration
 *  learnMoreLink: <string> - if present will render a 'Learn more' link under description
 *  allowOrgConnect: boolean - if this integration is allowed to connect for entire org (user must be Org Admin)
 *  orgConnectFlag: <string> - if present, will only render the org connect option if flag returns true (paired with allowOrgConnect)
 *  handleDisconnect: <Function> - Callback function that if present will be used for disconect instead default behavior
 *  handleOrgDisconnect: <Function> - Callback function that if present will be used for org disconect instead default behavior
 *  showOnIntegrationsPage: boolean - wheather or not to show this item on the integrations page
 *  mustRequestAccessFlag: <string> - feature flag on wheather or not to show a request access button
 */

// Images
import JiraLogo from 'Shared/images/JiraLogo.svg';
import FigmaLogo from 'Shared/images/figma-logo.svg';
import AsanaLogo from 'Shared/images/AsanaLogo.svg';
import HubspotLogo from 'Shared/images/HubspotLogo.svg';
import GoogleDriveLogo from 'Shared/images/GoogleDriveLogo.svg';
import GoogleCalendarLogo from 'Shared/images/GoogleCalendarLogo.svg';
import Outlook365 from 'Shared/images/outlook365.svg';
import SlackLogo from 'Shared/images/slackLogo.svg';
import MicrosoftTeamsLogo from 'Shared/images/MicrosoftTeamsLogo.svg';
import GoogleChatLogo from 'Shared/images/GoogleChatLogo.svg';
import MetaWorkplaceLogo from 'Shared/images/MetaWorkplaceLogo.svg';
import GitHubLogo from 'Shared/images/GithubLogo.svg';
import OneDriveLogo from 'Shared/images/OneDriveLogo.svg';
import ZoomLogo from 'Shared/images/ZoomLogo.svg';
import SalesforceLogo from 'Shared/images/salesforce-logo.svg';
import GmailLogo from 'Shared/images/GmailLogo.svg';
import HibobLogo from 'Shared/images/HibobLogo.svg';
import ServicenowLogo from 'Shared/images/ServicenowLogo.svg';
import ZapierLogo from 'Shared/images/ZapierLogo.svg';

// Components
import ConnectHibobModal from 'Dashboard/components/ConnectHibobModal/ConnectHibobModal';
import ConnectServicenowModal from 'Dashboard/components/ConnectServicenowModal/ConnectServicenowModal';

// Types
import { DataSourceCategories } from 'Shared/types/DataSource.type';

export enum IntegrationSources {
  ASANA = 'asana',
  HUBSPOT = 'hubspot',
  SALEFORCE = 'salesforce',
  JIRA = 'jira',
  SLACK = 'slack',
  MICROSOFT_TEAMS = 'microsoft_teams',
  GOOGLE_CHAT = 'google_chat',
  META_WORKPLACE = 'meta_workplace',
  FIGMA = 'figma',
  GITHUB = 'github',
  ZOOM = 'zoom',
  ZOOM_IN_CLIENT_APP = 'zoom_in_client_app',
  HIBOB = 'hibob',
  SERVICENOW = 'servicenow',
  ZAPIER = 'zapier',
  OFFICE_365_ONEDRIVE_INDIVIDUAL_AUTHORIZATION = 'office-365-onedrive-individual-authorization',
  OFFICE_365_CALEDNAR_WORKSPACE_SYNC_INDIVIDUAL_AUTHORIZATION = 'office-365-calendar-workspace-sync-individual-authorization',
  G_SUITE_CALENDAR_WORKSPACE_SYNC_INDIVIDUAL_AUTHORIZATION = 'g-suite-calendar-workspace-sync-individual-authorization',
  G_SUITE_DRIVE_INDIVIDUAL_AUTHORIZATION = 'g-suite-drive-individual-authorization',
  G_SUITE_EMAIL_INDIVIDUAL_AUTHORIZATION = 'g-suite-email-individual-authorization',
  G_SUITE_CALENDAR_INDIVIDUAL_FULL_AUTHORIZATION = 'g-suite-calendar-individual-full-authorization',
  SLACK_INDIVIDUAL_AUTHORIZATION = 'slack-individual-authorization',
  SLACK_AI = 'slack-ai',
}

export const IntegrationsList = {
  [IntegrationSources.ASANA]: {
    logo: AsanaLogo,
    categories: [DataSourceCategories.project_management, DataSourceCategories.collaboration],
    surfaceAI: true,
    aiToolkit: true,
    description: 'Create tasks in Asana directly from PI Perform',
  },
  [IntegrationSources.HUBSPOT]: {
    logo: HubspotLogo,
    flag: 'hubspotIntegration',
    categories: [DataSourceCategories.crm],
    description: 'Enrich HubSpot links in PI Perform',
    badge: 'Beta',
    aiToolkit: true,
  },
  [IntegrationSources.SALEFORCE]: {
    logo: SalesforceLogo,
    categories: [DataSourceCategories.crm],
    description: 'Enrich Salesforce links in PI Perform',
    surfaceAI: true,
    aiToolkit: true,
  },
  [IntegrationSources.JIRA]: {
    logo: JiraLogo,
    categories: [DataSourceCategories.development],
    description: 'Create tasks in Jira Cloud directly from PI Perform',
    surfaceAI: true,
    aiToolkit: true,
  },
  [IntegrationSources.FIGMA]: {
    logo: FigmaLogo,
    categories: [DataSourceCategories.design],
    badge: 'Beta',
    flag: 'figmaIntegration',
    mustRequestAccessFlag: 'figmaIntegrationMustRequestAccess',
    description: 'See Figma Activity in PI Perform',
  },
  [IntegrationSources.GITHUB]: {
    badge: 'Beta',
    flag: 'githubIntegration',
    mustRequestAccessFlag: 'githubIntegration',
    logo: GitHubLogo,
    categories: [DataSourceCategories.development],
    showOnIntegrationsPage: false,
  },
  [IntegrationSources.ZOOM_IN_CLIENT_APP]: {
    logo: ZoomLogo,
    flag: 'zoomInClientApp',
    categories: [DataSourceCategories.collaboration],
    learnMoreLink: 'https://marketplace.zoom.us/apps/xwlydKrzRnOvk-iYtIX83w',
    description: 'Embed PI Perform in the Zoom Client',
    handleDisconnect: () => {
      window.location.href = '/zoomapp_documentation';
    },
  },
  [IntegrationSources.OFFICE_365_ONEDRIVE_INDIVIDUAL_AUTHORIZATION]: {
    categories: [DataSourceCategories.collaboration],
    logo: OneDriveLogo,
    description: 'Show document names for OneDrive and Sharepoint links',
    surfaceAI: true,
  },
  [IntegrationSources.OFFICE_365_CALEDNAR_WORKSPACE_SYNC_INDIVIDUAL_AUTHORIZATION]: {
    flag: 'googleCalendar',
    categories: [DataSourceCategories.calendar],
    logo: Outlook365,
  },
  [IntegrationSources.G_SUITE_CALENDAR_WORKSPACE_SYNC_INDIVIDUAL_AUTHORIZATION]: {
    flag: 'googleCalendar',
    categories: [DataSourceCategories.calendar],
    logo: GoogleCalendarLogo,
  },
  [IntegrationSources.G_SUITE_DRIVE_INDIVIDUAL_AUTHORIZATION]: {
    logo: GoogleDriveLogo,
    categories: [DataSourceCategories.collaboration],
    surfaceAI: true,
  },
  [IntegrationSources.G_SUITE_EMAIL_INDIVIDUAL_AUTHORIZATION]: {
    logo: GmailLogo,
    categories: [DataSourceCategories.collaboration],
  },
  [IntegrationSources.G_SUITE_CALENDAR_INDIVIDUAL_FULL_AUTHORIZATION]: {
    logo: GoogleCalendarLogo,
    categories: [DataSourceCategories.crm],
  },
  [IntegrationSources.SLACK]: {
    logo: SlackLogo,
    agendaAiDescription: 'Receive notifications and analyze conversations directly in Slack',
    description: 'Receive notifications directly in Slack',
    categories: [DataSourceCategories.communication],
    allowOrgConnect: true,
    agendaAiDatasourceSlug: IntegrationSources.SLACK_AI,
    orgConnectFlag: 'slackOrgWideNotifications',
    aiToolkit: true,
  },
  [IntegrationSources.MICROSOFT_TEAMS]: {
    logo: MicrosoftTeamsLogo,
    description: 'Receive notifications directly in Teams',
    categories: [DataSourceCategories.communication],
  },
  [IntegrationSources.GOOGLE_CHAT]: {
    logo: GoogleChatLogo,
    description: 'Receive notifications directly in Google Chat',
    categories: [DataSourceCategories.communication],
    flag: 'googleChatIntegration',
  },
  [IntegrationSources.META_WORKPLACE]: {
    logo: MetaWorkplaceLogo,
    description: 'Receive notifications directly in Meta Workplace',
    categories: [DataSourceCategories.communication],
    flag: 'featureFlagMetaWorkplaceIntegration',
    allowOrgConnect: false,
  },
  [IntegrationSources.HIBOB]: {
    logo: HibobLogo,
    description: 'Provision PI Perform seats from Hibob',
    categories: [DataSourceCategories.hris],
    connectModal: ConnectHibobModal,
    showOnIntegrationsPage: false,
  },
  [IntegrationSources.SLACK_AI]: {
    showOnIntegrationsPage: false,
  },
  [IntegrationSources.SERVICENOW]: {
    logo: ServicenowLogo,
    description: 'Create Servicenow records from PI Perform',
    categories: [DataSourceCategories.project_management],
    connectModal: ConnectServicenowModal,
    showOnIntegrationsPage: true,
    badge: 'Beta',
    flag: 'servicenowIntegration',
  },
  [IntegrationSources.SLACK_INDIVIDUAL_AUTHORIZATION]: {
    showOnIntegrationsPage: false,
  },
  [IntegrationSources.ZAPIER]: {
    description: 'Automate your workflows with Zapier',
    flag: 'zapierIntegrationMustRequestAccess',
    mustRequestAccessFlag: 'zapierIntegrationMustRequestAccess',
    logo: ZapierLogo,
    categories: [DataSourceCategories.development],
  },
  [IntegrationSources.ZOOM]: {
    showOnIntegrationsPage: false,
  },
};
